import { useEffect, useRef, useState } from "react";

import {
  ImagePlus,
  MapPin,
  PhilippinePeso,
  Weight,
  HeartPulse,
  Calendar,
  Tag,
  FileText,
  ArrowLeft,
  ShieldCheck,
  UploadCloud,
  AlertTriangle,
  Hash,
  X,
} from "lucide-react";

import { Link, useNavigate, useParams } from "react-router-dom";
import { getDocumentRequirement, formatRequirementMessage } from "../utils/livestockDocRequirements";
import {
  LIVESTOCK_TYPES,
  FARM_PRODUCT_TYPES,
  ALL_KNOWN_TYPES,
  OTHER_LIVESTOCK,
  OTHER_PRODUCT,
  UNITS,
  unitsFor,
  isValidUnitFor,
  getPricePerQuantityUnit,
  defaultUnitFor,
  getCategory,
} from "../utils/productCatalog";
import { API_URL } from "../config";
import useGoBack from "../utils/useGoBack";
import { loadListingDraft, saveListingDraft, clearListingDraft } from "../utils/listingDraft";
import "./Post.css";

const EMPTY_FORM = {
  unit: "head",
  breed: "",
  age: "",
  weight: "",
  price: "",
  quantity: "",
  healthStatus: "",
  location: "",
  description: "",
  imageUrl: "",
};

// Fields that feed an open deal's price, so a saved draft mustn't change
// them while the listing has one (the form locks them too).
const DEAL_LOCKED_FIELDS = ["unit", "price", "weight"];

function Post() {
  const navigate = useNavigate();
  const goBack = useGoBack("/listings");
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingListing, setLoadingListing] = useState(isEditMode);
  const [message, setMessage] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isVerified, setIsVerified] = useState(true);
  // System Settings > "Require document verification" (sent with the
  // farmer profile); when off, supporting documents are optional.
  const [documentsRequired, setDocumentsRequired] = useState(true);
  // Open deals on this listing (edit mode): their price is worked out from
  // the type, unit, price and weight, so those are locked until they close.
  const [openDeals, setOpenDeals] = useState(0);

  // typeChoice is the dropdown value: a known type, or one of the two
  // "Other" options, in which case the farmer types the name in customType.
  const [typeChoice, setTypeChoice] = useState("");
  const [customType, setCustomType] = useState("");

  const [formData, setFormData] = useState(EMPTY_FORM);

  // Auto-saved draft (utils/listingDraft.js). edited is set once the farmer
  // changes something, so just opening the form never writes a draft;
  // original is what the form started from, for "Discard changes".
  const edited = useRef(false);
  const original = useRef({ typeChoice: "", customType: "", formData: EMPTY_FORM });
  const [restoredAt, setRestoredAt] = useState(null);

  const applyForm = ({ typeChoice: nextType, customType: nextCustom, formData: nextForm }) => {
    setTypeChoice(nextType);
    setCustomType(nextCustom);
    setFormData(nextForm);
  };

  // Puts a saved draft over what the form started with. With an open deal,
  // the type and the price fields keep their saved-on-server values.
  const restoreDraft = (start, locked) => {
    const draft = loadListingDraft(id);
    if (!draft) return;

    const formFromDraft = { ...start.formData, ...draft.formData };
    if (locked) {
      DEAL_LOCKED_FIELDS.forEach((field) => (formFromDraft[field] = start.formData[field]));
    }

    applyForm({
      typeChoice: locked ? start.typeChoice : draft.typeChoice ?? start.typeChoice,
      customType: locked ? start.customType : draft.customType ?? start.customType,
      formData: formFromDraft,
    });
    setRestoredAt(draft.savedAt || Date.now());
  };

  const handleDiscardDraft = () => {
    clearListingDraft(id);
    edited.current = false;
    applyForm(original.current);
    setRestoredAt(null);
  };

  useEffect(() => {
    if (!edited.current) return;
    const current = { typeChoice, customType, formData };
    if (JSON.stringify(current) === JSON.stringify(original.current)) {
      clearListingDraft(id);
    } else {
      saveListingDraft(id, current);
    }
  }, [id, typeChoice, customType, formData]);

  useEffect(() => {
    if (isEditMode) return;
    restoreDraft(original.current, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchListing = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          navigate("/login");
          return;
        }

        const res = await fetch(`${API_URL}/api/listings/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();

        if (!res.ok) {
          setMessage(data.message || "Failed to load listing");
          setLoadingListing(false);
          return;
        }

        const savedType = data.livestock_type || "";
        let loadedChoice = "";
        let loadedCustom = "";
        if (ALL_KNOWN_TYPES.includes(savedType)) {
          loadedChoice = savedType;
        } else if (savedType) {
          loadedChoice = data.category === "product" ? OTHER_PRODUCT : OTHER_LIVESTOCK;
          loadedCustom = savedType;
        }

        // Older live-animal listings may be priced "per kilo" with the
        // quantity counted in kg, or have a free-text weight ("120 kg").
        // Leave the unit blank so the farmer re-picks it (and re-checks
        // the quantity) instead of the meaning changing silently.
        const savedCategory = getCategory(savedType, data.category);
        const savedUnit = data.unit || "head";
        const isSavedLivestock = savedCategory === "livestock";

        const loadedForm = {
          unit: isValidUnitFor(savedUnit, savedCategory) ? savedUnit : "",
          breed: data.breed || "",
          age: data.age || "",
          weight: isSavedLivestock ? String(parseFloat(data.weight) || "") : data.weight || "",
          price: data.price || "",
          quantity: data.quantity || "",
          healthStatus: data.health_status || "",
          location: data.location || "",
          description: data.description || "",
          imageUrl: "",
        };

        const loadedOpenDeals = Number(data.open_deals) || 0;
        original.current = { typeChoice: loadedChoice, customType: loadedCustom, formData: loadedForm };
        applyForm(original.current);
        restoreDraft(original.current, loadedOpenDeals > 0);

        setExistingImageUrl(data.image_url || "");
        setOpenDeals(loadedOpenDeals);

        try {
          const docs = data.documents ? JSON.parse(data.documents) : [];
          setExistingDocuments(Array.isArray(docs) ? docs : []);
        } catch (error) {
          setExistingDocuments([]);
        }

        setLoadingListing(false);
      } catch (error) {
        setMessage("Cannot connect to backend server");
        setLoadingListing(false);
      }
    };

    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isEditMode) return;

    const checkVerification = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          navigate("/login");
          return;
        }

        const res = await fetch(`${API_URL}/api/farmer/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();

        if (res.ok) {
          setIsVerified(Boolean(data.is_verified));
          setDocumentsRequired(data.documentsRequired !== false);
        }
      } catch (error) {
        // If this check fails, let the actual submit attempt surface the error.
      }
    };

    checkVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  useEffect(() => {
    if (!selectedImage) {
      setImagePreviewUrl("");
      return;
    }

    const url = URL.createObjectURL(selectedImage);
    setImagePreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  const handleChange = (e) => {
    edited.current = true;
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const isOtherChoice = typeChoice === OTHER_LIVESTOCK || typeChoice === OTHER_PRODUCT;
  const productType = isOtherChoice ? customType.trim() : typeChoice;
  const category =
    typeChoice === OTHER_PRODUCT ? "product" : getCategory(typeChoice, "livestock");
  const isProduct = category === "product";
  const unitInfo = UNITS.find((u) => u.value === formData.unit) || UNITS[0];
  const dealLocked = openDeals > 0;
  const liveWeightEstimate =
    Math.round(getPricePerQuantityUnit({ ...formData, price: Number(formData.price) || 0 }) * 100) / 100;

  const handleTypeChange = (e) => {
    const choice = e.target.value;
    edited.current = true;
    setTypeChoice(choice);

    const nextCategory = choice === OTHER_PRODUCT ? "product" : getCategory(choice, "livestock");
    setFormData((prev) => ({
      ...prev,
      unit: defaultUnitFor(choice, nextCategory),
      // Kg per head for animals vs. free-text size for products.
      weight: nextCategory === category ? prev.weight : "",
    }));
  };

  const handleImageChange = (e) => {
    setSelectedImage(e.target.files[0] || null);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setExistingImageUrl("");
  };

  const handleDocumentsChange = (e) => {
    setSelectedDocuments(Array.from(e.target.files));
  };

  const handleRemoveSelectedDocument = (index) => {
    setSelectedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePost = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!productType) {
      setMessage("Please enter what you're selling.");
      return;
    }

    const requirement = getDocumentRequirement(productType, category);
    const documentCount = selectedDocuments.length || existingDocuments.length;

    if (documentsRequired && requirement && documentCount < requirement.minDocuments) {
      setMessage(formatRequirementMessage(productType, category));
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });
      payload.append("livestockType", productType);
      payload.append("category", category);

      if (selectedImage) {
        payload.append("image", selectedImage);
      }

      selectedDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await fetch(
        isEditMode
          ? `${API_URL}/api/listings/${id}`
          : `${API_URL}/api/listings`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: payload,
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.message || (isEditMode ? "Failed to update listing" : "Failed to create listing"));
        setLoading(false);
        return;
      }

      edited.current = false;
      clearListingDraft(id);
      alert(data.message || (isEditMode ? "Listing updated successfully!" : "Listing published successfully!"));
      navigate("/listings");
    } catch (error) {
      setMessage("Cannot connect to backend server");
    }

    setLoading(false);
  };

  if (loadingListing) {
    return <h2 style={{ padding: "30px" }}>Loading listing...</h2>;
  }

  return (
    <div className="premium-post-page">
      <div className="premium-post-header">
        <div className="header-left">
          <button type="button" className="back-btn" onClick={goBack} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>

          <div>
            <span className="page-tag">FARM MARKETPLACE</span>
            <h1>{isEditMode ? "Edit Listing" : "Create New Listing"}</h1>
            <p>
              {isEditMode
                ? "Update your listing details, photo, or documents."
                : "Post livestock or backyard farm products like eggs, fruits, and vegetables to reach verified buyers."}
            </p>
          </div>
        </div>
      </div>

      <div className="premium-post-card">
        {!isEditMode && !isVerified && (
          <div className="form-alert warning">
            <AlertTriangle size={18} />
            <p>
              Your account isn't verified yet, so you can't post a listing. Submit your
              government ID and barangay certificate on your{" "}
              <Link to="/profile">Profile page</Link> to get verified.
            </p>
          </div>
        )}

        {restoredAt && (
          <div className="form-alert info">
            <FileText size={18} />
            <p>
              We restored the details you entered on{" "}
              {new Date(restoredAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} but didn't{" "}
              {isEditMode ? "save" : "post"}. Please attach the photo and certificate again.{" "}
              <button type="button" className="form-alert-action" onClick={handleDiscardDraft}>
                Discard changes
              </button>
            </p>
          </div>
        )}

        {message && (
          <div className="form-alert error">
            <AlertTriangle size={18} />
            <p>{message}</p>
          </div>
        )}

        <form onSubmit={handlePost}>
          <div className="premium-upload-box">
            {imagePreviewUrl || existingImageUrl ? (
              <div className="upload-preview">
                <img src={imagePreviewUrl || existingImageUrl} alt="Product" />
                <button
                  type="button"
                  className="upload-preview-remove"
                  onClick={handleRemoveImage}
                  title="Remove photo"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <div className="upload-icon">
                  <UploadCloud size={26} />
                </div>

                <h3>Upload Product Photo</h3>

                <p>Click Choose Photo to select a photo of what you're selling.</p>
              </>
            )}

            <label className="upload-btn" htmlFor="livestockImage">
              <ImagePlus size={18} />
              {imagePreviewUrl || existingImageUrl ? "Change Photo" : "Choose Photo"}
            </label>

            <input
              id="livestockImage"
              className="visually-hidden"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />

            <input
              name="imageUrl"
              className="upload-url-fallback"
              type="text"
              placeholder="Or paste an image URL instead"
              value={formData.imageUrl}
              onChange={handleChange}
              disabled={!!selectedImage}
            />
          </div>

          <div className="premium-upload-box">
            <div className="upload-icon">
              <FileText size={26} />
            </div>

            {isProduct ? (
              <>
                <h3>
                  Supporting Document <span className="optional-mark">(Optional)</span>
                </h3>

                <p>Farm products don't need a health certificate, but you can attach any permit or certificate you have.</p>
              </>
            ) : (
              <>
                <h3>Upload Barangay Health Certificate</h3>

                <p>Attach your barangay health certificate for this animal for MAO verification.</p>

                {productType && (
                  <p className="doc-requirement-hint">
                    {documentsRequired
                      ? formatRequirementMessage(productType, category)
                      : "Optional — MAO currently doesn't require documents for listings."}
                  </p>
                )}
              </>
            )}

            <label className="upload-btn" htmlFor="livestockDocuments">
              <FileText size={18} />
              {isProduct ? "Choose Document" : "Choose Certificate"}
            </label>

            <input
              id="livestockDocuments"
              className="visually-hidden"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleDocumentsChange}
            />

            {selectedDocuments.length > 0 ? (
              <div className="doc-chip-list">
                <span className="doc-chip-label">
                  {existingDocuments.length > 0 ? "Selected (replaces the current one):" : "Selected:"}
                </span>

                {selectedDocuments.map((file, index) => (
                  <span className="doc-chip" key={index}>
                    <FileText size={14} />
                    <span className="doc-chip-name">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSelectedDocument(index)}
                      title="Remove"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : existingDocuments.length > 0 ? (
              <div className="doc-chip-list">
                <span className="doc-chip-label">
                  {isProduct ? "Current document:" : "Current certificate:"}
                </span>

                {existingDocuments.map((docUrl, index) => (
                  <a
                    className="doc-chip"
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={index}
                  >
                    <FileText size={14} />
                    <span className="doc-chip-name">
                      {/* Listings from before the one-document limit may still have several. */}
                      {existingDocuments.length > 1
                        ? `Document ${index + 1}`
                        : isProduct
                        ? "View document"
                        : "View certificate"}
                    </span>
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <div className="section-title">
            <div className="section-title-icon">
              <ShieldCheck size={16} />
            </div>
            <div>
              <h4>{isProduct ? "Product Information" : "Livestock Information"}</h4>
              <p>Accurate details help MAO verify your listing faster.</p>
            </div>
          </div>

          {dealLocked && (
            <p className="deal-lock-note">
              <AlertTriangle size={16} />
              This listing has {openDeals === 1 ? "an open deal" : `${openDeals} open deals`}, so the type,
              selling unit, price and weight are locked until {openDeals === 1 ? "it is" : "they are"}{" "}
              completed or cancelled. You can still update the other details.
            </p>
          )}

          <div className="premium-form-grid">
            <div className="form-group">
              <label>What are you selling?</label>

              <div className="input-wrapper">
                <Tag size={18} />

                <select value={typeChoice} onChange={handleTypeChange} disabled={dealLocked} required>
                  <option value="">Choose a product type</option>
                  <optgroup label="Livestock">
                    {LIVESTOCK_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Farm Products">
                    {FARM_PRODUCT_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Other">
                    <option value={OTHER_LIVESTOCK}>Other animal (type your own)</option>
                    <option value={OTHER_PRODUCT}>Other farm product (type your own)</option>
                  </optgroup>
                </select>
              </div>
            </div>

            {isOtherChoice && (
              <div className="form-group">
                <label>{typeChoice === OTHER_PRODUCT ? "Product Name" : "Animal Type"}</label>

                <div className="input-wrapper">
                  <Tag size={18} />

                  <input
                    type="text"
                    maxLength={50}
                    placeholder={
                      typeChoice === OTHER_PRODUCT
                        ? "Example: Mushrooms, Rice, Peanuts"
                        : "Example: Rabbit, Turkey, Sheep"
                    }
                    value={customType}
                    onChange={(e) => {
                      edited.current = true;
                      setCustomType(e.target.value);
                    }}
                    disabled={dealLocked}
                    required
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>
                {isProduct ? "Variety" : "Breed"}
                {isProduct && <span className="optional-mark"> (Optional)</span>}
              </label>

              <div className="input-wrapper">
                <Tag size={18} />

                <input
                  name="breed"
                  maxLength={100}
                  type="text"
                  placeholder={isProduct ? "Example: Native, Lakatan, Carabao mango" : "Enter livestock breed"}
                  value={formData.breed}
                  onChange={handleChange}
                  required={!isProduct}
                />
              </div>
            </div>

            {!isProduct && (
            <div className="form-group">
              <label>Age</label>

              <div className="input-wrapper">
                <Calendar size={18} />

                <input
                  name="age"
                  maxLength={50}
                  type="text"
                  placeholder="Example: 8 months"
                  value={formData.age}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            )}

            <div className="form-group">
              <label>
                {isProduct ? "Size / Weight" : "Average Weight per Head (kg)"}
                {isProduct && <span className="optional-mark"> (Optional)</span>}
              </label>

              <div className="input-wrapper">
                <Weight size={18} />

                {isProduct ? (
                  <input
                    name="weight"
                    type="text"
                    placeholder="Example: Large, 60g per egg"
                    maxLength={50}
                    value={formData.weight}
                    onChange={handleChange}
                  />
                ) : (
                  <input
                    name="weight"
                    type="number"
                    min="0.1"
                    max="5000"
                    step="0.1"
                    placeholder="Example: 100"
                    disabled={dealLocked}
                    value={formData.weight}
                    onChange={handleChange}
                    required
                  />
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Price {unitInfo.label}</label>

              <div className="input-wrapper">
                <PhilippinePeso size={18} />

                <input
                  name="price"
                  type="number"
                  placeholder={`Price ${unitInfo.label}`}
                  disabled={dealLocked}
                  value={formData.price}
                  onChange={handleChange}
                  required
                />
              </div>

              {formData.unit === "kg_live" && (
                <p className="price-estimate">
                  {liveWeightEstimate > 0
                    ? `≈ ₱${liveWeightEstimate.toLocaleString()} per head at the average weight. `
                    : ""}
                  The final price is based on the actual weight at pickup.
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Selling Unit</label>

              <div className="input-wrapper">
                <Hash size={18} />

                <select name="unit" value={formData.unit} onChange={handleChange} disabled={dealLocked} required>
                  {!formData.unit && <option value="">Choose a selling unit</option>}
                  {unitsFor(category).map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Quantity Available</label>

              <div className="input-wrapper">
                <Hash size={18} />

                <input
                  name="quantity"
                  type="number"
                  min="1"
                  step="1"
                  placeholder={`Number of ${unitInfo.plural} available`}
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                {isProduct ? "Condition" : "Health Status"}
                {isProduct && <span className="optional-mark"> (Optional)</span>}
              </label>

              <div className="input-wrapper">
                <HeartPulse size={18} />

                <input
                  name="healthStatus"
                  maxLength={100}
                  type="text"
                  placeholder={isProduct ? "Fresh / Organic / Newly harvested" : "Healthy / Vaccinated"}
                  value={formData.healthStatus}
                  onChange={handleChange}
                  required={!isProduct}
                />
              </div>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Farm Location</label>

            <div className="input-wrapper">
              <MapPin size={18} />

              <input
                name="location"
                maxLength={150}
                type="text"
                placeholder="Veruela, Agusan del Sur"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>
              Description <span className="optional-mark">(Optional)</span>
            </label>

            <div className="textarea-wrapper">
              <FileText size={18} />

              <textarea
                name="description"
                maxLength={2000}
                rows="6"
                placeholder={
                  isProduct
                    ? "Describe your product: how it was grown or raised, harvest date, pickup or delivery options..."
                    : "Provide complete livestock details, feeding history, vaccination records, and additional information..."
                }
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>
          </div>

          <div className="post-actions">
            <button
              className="post-submit-btn"
              type="submit"
              disabled={loading || (!isEditMode && !isVerified)}
            >
              {loading
                ? isEditMode
                  ? "Saving..."
                  : "Publishing..."
                : isEditMode
                ? "Save Changes"
                : "Publish Listing"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Post;